import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Award,
  Download,
  Eye,
  Pencil,
  Save,
  Plus,
  X,
  FileText,
  Trash2,
  CheckCircle,
  Printer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CertificateEditor = ({
  enrollment,
  onClose,
  getAuthHeaders,
  onCertificateCreated
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [newAchievement, setNewAchievement] = useState("");
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    puppy_name: "",
    owner_name: "",
    program_name: "",
    completion_date: "",
    trainer_notes: "",
    achievements: [],
    status: "draft"
  });

  useEffect(() => {
    loadCertificate();
  }, [enrollment?.id]);

  const loadCertificate = async () => {
    if (!enrollment?.id) return;
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/graduation-certificates/enrollment/${enrollment.id}`, { headers });
      setCertificate(res.data);
      setFormData({
        puppy_name: res.data.puppy_name || "",
        owner_name: res.data.owner_name || "",
        program_name: res.data.program_name || "",
        completion_date: res.data.completion_date || "",
        trainer_notes: res.data.trainer_notes || "",
        achievements: res.data.achievements || [],
        status: res.data.status || "draft"
      });
      setIsEditing(false);
    } catch (error) {
      if (error.response?.status === 404) {
        // No certificate exists yet, prepare for creation
        setCertificate(null);
        setFormData({
          puppy_name: enrollment.puppy_name || "",
          owner_name: enrollment.owner_name || "",
          program_name: enrollment.program_name || "",
          completion_date: new Date().toISOString().split('T')[0],
          trainer_notes: "",
          achievements: [],
          status: "draft"
        });
        setIsEditing(true);
      } else {
        toast.error("Failed to load certificate");
      }
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      
      if (certificate) {
        // Update existing
        await axios.put(`${API}/graduation-certificates/${certificate.id}`, formData, { headers });
        toast.success("Certificate saved!");
      } else {
        // Create new
        await axios.post(`${API}/graduation-certificates`, {
          enrollment_id: enrollment.id,
          ...formData
        }, { headers });
        toast.success("Certificate created!");
      }
      
      await loadCertificate();
      setIsEditing(false);
      if (onCertificateCreated) onCertificateCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save certificate");
    }
    setSaving(false);
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/graduation-certificates/${certificate.id}`, {
        ...formData,
        status: "finalized"
      }, { headers });
      toast.success("Certificate finalized! Client can now view and download it.");
      await loadCertificate();
      if (onCertificateCreated) onCertificateCreated();
    } catch (error) {
      toast.error("Failed to finalize certificate");
    }
    setSaving(false);
  };

  const handlePreview = () => {
    if (!certificate) {
      toast.error("Save the certificate first to preview");
      return;
    }
    window.open(`${API}/graduation-certificates/${certificate.id}/preview`, '_blank');
  };

  const handleDownload = () => {
    if (!certificate) {
      toast.error("Save the certificate first to download");
      return;
    }
    window.open(`${API}/graduation-certificates/${certificate.id}/pdf`, '_blank');
  };

  const handlePrintPreview = () => {
    setShowPrintPreview(true);
  };

  const handlePrint = () => {
    window.print();
  };

  const addAchievement = () => {
    if (newAchievement.trim() && formData.achievements.length < 5) {
      setFormData(prev => ({
        ...prev,
        achievements: [...prev.achievements, newAchievement.trim()]
      }));
      setNewAchievement("");
    }
  };

  const removeAchievement = (index) => {
    setFormData(prev => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-2 border-olive border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Award className="w-6 h-6 text-olive" />
          <div>
            <h3 className="font-heading text-lg text-charcoal">Graduation Certificate</h3>
            <p className="text-sm text-stone">{enrollment.puppy_name} - {enrollment.program_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {certificate && (
            <Badge variant={certificate.status === "finalized" ? "default" : "secondary"}>
              {certificate.status === "finalized" ? "Finalized" : "Draft"}
            </Badge>
          )}
          {!certificate && <Badge variant="outline">New</Badge>}
        </div>
      </div>

      {/* Certificate Preview Card */}
      <div className="bg-gradient-to-b from-sand/30 to-sand/10 rounded-xl p-6 border border-olive/20">
        <div className="text-center space-y-3">
          <p className="text-olive font-heading text-xl italic">The Pack Standard</p>
          <p className="text-xs tracking-widest text-olive/70">PUPPY ACADEMY</p>
          <h4 className="text-2xl font-heading text-charcoal">Certificate of Graduation</h4>
          
          <div className="py-4">
            <p className="text-charcoal text-3xl font-heading italic text-olive">
              {formData.puppy_name || "Puppy Name"}
            </p>
            <p className="text-stone mt-1">with owner {formData.owner_name || "Owner Name"}</p>
          </div>
          
          <div className="inline-block bg-sand px-6 py-2 rounded-full">
            <p className="font-medium text-charcoal">{formData.program_name || "Program"}</p>
          </div>
          
          {formData.achievements.length > 0 && (
            <div className="pt-4">
              <p className="text-olive text-sm italic mb-2">Special Recognition:</p>
              {formData.achievements.map((achievement, idx) => (
                <p key={`achievement-preview-${idx}-${achievement.slice(0,10)}`} className="text-charcoal text-sm">★ {achievement}</p>
              ))}
            </div>
          )}
          
          {formData.trainer_notes && (
            <p className="text-stone italic text-sm pt-2">"{formData.trainer_notes}"</p>
          )}
          
          <p className="text-stone text-sm pt-4">Awarded on {formData.completion_date || "Date"}</p>
        </div>
      </div>

      {/* Edit Form */}
      {isEditing ? (
        <div className="space-y-4 border-t pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Puppy Name</Label>
              <Input
                value={formData.puppy_name}
                onChange={(e) => setFormData(prev => ({ ...prev, puppy_name: e.target.value }))}
                placeholder="Puppy's name"
                data-testid="cert-puppy-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Owner Name</Label>
              <Input
                value={formData.owner_name}
                onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                placeholder="Owner's name"
                data-testid="cert-owner-name"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Program</Label>
              <Select
                value={formData.program_name}
                onValueChange={(v) => setFormData(prev => ({ ...prev, program_name: v }))}
              >
                <SelectTrigger data-testid="cert-program">
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Puppy Foundations">Puppy Foundations</SelectItem>
                  <SelectItem value="Puppy Development">Puppy Development</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Completion Date</Label>
              <Input
                type="date"
                value={formData.completion_date}
                onChange={(e) => setFormData(prev => ({ ...prev, completion_date: e.target.value }))}
                data-testid="cert-date"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Trainer Notes (Optional)</Label>
            <Textarea
              value={formData.trainer_notes}
              onChange={(e) => setFormData(prev => ({ ...prev, trainer_notes: e.target.value }))}
              placeholder="Add a personal note about the puppy's journey..."
              rows={2}
              data-testid="cert-notes"
            />
          </div>

          <div className="space-y-2">
            <Label>Achievements (Optional, max 5)</Label>
            <div className="flex gap-2">
              <Input
                value={newAchievement}
                onChange={(e) => setNewAchievement(e.target.value)}
                placeholder="e.g., Best recall in class"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAchievement())}
                disabled={formData.achievements.length >= 5}
                data-testid="cert-achievement-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={addAchievement}
                disabled={formData.achievements.length >= 5 || !newAchievement.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.achievements.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.achievements.map((achievement, idx) => (
                  <Badge key={`achievement-edit-${idx}-${achievement.slice(0,10)}`} variant="secondary" className="flex items-center gap-1">
                    {achievement}
                    <button onClick={() => removeAchievement(idx)} className="ml-1 hover:text-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button onClick={handleSave} className="btn-primary flex-1" disabled={saving} data-testid="save-cert-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Saving..." : "Save Draft"}
            </Button>
            {certificate && (
              <Button variant="outline" onClick={() => { loadCertificate(); setIsEditing(false); }}>
                Cancel
              </Button>
            )}
          </div>
        </div>
      ) : (
        /* View Mode Actions */
        <div className="flex flex-wrap gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-cert-btn">
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" onClick={handlePrintPreview} data-testid="print-preview-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print Preview
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={!certificate} data-testid="preview-cert-btn">
            <Eye className="w-4 h-4 mr-2" />
            PDF Preview
          </Button>
          <Button variant="outline" onClick={handleDownload} disabled={!certificate} data-testid="download-cert-btn">
            <Download className="w-4 h-4 mr-2" />
            Download
          </Button>
          
          {certificate && certificate.status !== "finalized" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="bg-olive hover:bg-olive/90" data-testid="finalize-cert-btn">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Finalize & Share
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Finalize Certificate?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Once finalized, the client will be able to view and download this certificate from their portal. You can still edit it later if needed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleFinalize} className="bg-olive">
                    Finalize
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          
          {certificate && certificate.status === "finalized" && (
            <Badge variant="default" className="bg-green-600 self-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Shared with Client
            </Badge>
          )}
        </div>
      )}

      {/* Full-screen Print Preview Modal */}
      {showPrintPreview && (
        <div className="fixed inset-0 z-50 bg-stone/80 flex items-center justify-center print:bg-white print:p-0">
          <div className="bg-white max-w-[297mm] w-full print:max-w-none print:w-full">
            {/* Print Preview Header - Hidden when printing */}
            <div className="bg-charcoal text-white p-4 flex items-center justify-between print:hidden">
              <h3 className="font-heading text-lg">Print Preview - A4 Landscape</h3>
              <div className="flex gap-2">
                <Button onClick={handlePrint} className="bg-olive hover:bg-olive/90">
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button variant="outline" className="text-white border-white hover:bg-white/20" onClick={() => setShowPrintPreview(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Close
                </Button>
              </div>
            </div>
            
            {/* A4 Landscape Certificate - Actual print content */}
            <div className="print-certificate aspect-[297/210] bg-[#F9F9F7] p-8 relative overflow-hidden print:p-12" style={{ minHeight: '210mm' }}>
              {/* Outer Border */}
              <div className="absolute inset-6 border-[3px] border-[#556B2F] rounded-sm print:inset-8">
                {/* Inner Border */}
                <div className="absolute inset-3 border border-[#EBE5CE]"></div>
              </div>
              
              {/* Corner Decorations */}
              <div className="absolute top-8 left-8 w-3 h-3 rounded-full bg-[#556B2F] print:top-10 print:left-10"></div>
              <div className="absolute top-8 right-8 w-3 h-3 rounded-full bg-[#556B2F] print:top-10 print:right-10"></div>
              <div className="absolute bottom-8 left-8 w-3 h-3 rounded-full bg-[#556B2F] print:bottom-10 print:left-10"></div>
              <div className="absolute bottom-8 right-8 w-3 h-3 rounded-full bg-[#556B2F] print:bottom-10 print:right-10"></div>
              
              {/* Content */}
              <div className="relative z-10 h-full flex flex-col items-center justify-between py-8 text-center">
                {/* Header */}
                <div>
                  <h1 className="font-serif text-4xl italic text-[#556B2F] mb-1">The Pack Standard</h1>
                  <p className="text-xs tracking-[0.3em] text-[#556B2F]/70">PUPPY ACADEMY</p>
                </div>
                
                {/* Title */}
                <div>
                  <h2 className="font-serif text-4xl font-bold text-[#2D2D2A] mb-2">Certificate of Graduation</h2>
                  <div className="w-48 h-0.5 bg-[#C9A227] mx-auto"></div>
                </div>
                
                {/* Main Content */}
                <div className="space-y-4">
                  <p className="text-lg text-[#2D2D2A]">This certifies that</p>
                  <p className="font-serif text-5xl italic text-[#556B2F]">{formData.puppy_name || "Puppy Name"}</p>
                  <p className="text-lg text-[#2D2D2A]">with their owner {formData.owner_name || "Owner Name"}</p>
                  <p className="text-lg text-[#2D2D2A]">has successfully completed the</p>
                  <div className="inline-block bg-[#EBE5CE] px-8 py-2 rounded-full">
                    <p className="font-serif text-xl font-bold text-[#2D2D2A]">{formData.program_name || "Program"}</p>
                  </div>
                </div>
                
                {/* Achievements */}
                {formData.achievements.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm italic text-[#556B2F]">Special Recognition:</p>
                    <div className="space-y-1">
                      {formData.achievements.map((achievement, idx) => (
                        <p key={`achievement-print-${idx}-${achievement.slice(0,10)}`} className="text-sm text-[#2D2D2A]">★ {achievement}</p>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Trainer Notes */}
                {formData.trainer_notes && (
                  <p className="text-sm italic text-[#2D2D2A] max-w-md">"{formData.trainer_notes}"</p>
                )}
                
                {/* Footer */}
                <div className="space-y-2">
                  <p className="text-base text-[#2D2D2A]">Awarded on {formData.completion_date || "Date"}</p>
                  <div className="w-32 h-px bg-[#2D2D2A] mx-auto"></div>
                  <p className="text-xs text-[#2D2D2A]">The Pack Standard Puppy Academy</p>
                  <p className="text-xs italic text-[#556B2F]">Sunshine Coast Hinterland, Australia</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Dialog wrapper for easy use
export const CertificateEditorDialog = ({
  open,
  onOpenChange,
  enrollment,
  getAuthHeaders,
  onCertificateCreated
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <CertificateEditor
          enrollment={enrollment}
          onClose={() => onOpenChange(false)}
          getAuthHeaders={getAuthHeaders}
          onCertificateCreated={onCertificateCreated}
        />
      </DialogContent>
    </Dialog>
  );
};
